import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Target, ChevronRight, Lock } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import API from '../utils/api';

// Predefined colors for strategies
const STRATEGY_COLORS = [
  '#06b6d4', // Cyan
  '#22c55e', // Green
  '#eab308', // Yellow
  '#f97316', // Orange
  '#ef4444', // Red
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#3b82f6', // Blue
  '#14b8a6', // Teal
  '#f59e0b', // Amber
];

const StrategiesPage = () => {
  const { user, logout } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [strategies, setStrategies] = useState([]);
  const [strategyLimit, setStrategyLimit] = useState(1);
  const [canCreate, setCanCreate] = useState(true);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [strategyStats, setStrategyStats] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: STRATEGY_COLORS[0]
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchStrategies();
  }, []);

  const fetchStrategies = async () => {
    try {
      const response = await fetch(`${API}/strategies`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStrategies(data.strategies || []);
        setStrategyLimit(data.limit);
        setCanCreate(data.can_create);
      }
    } catch (error) {
      console.error('Error fetching strategies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error(language === 'de' ? 'Name ist erforderlich' : 'Name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/strategies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success(language === 'de' ? 'Strategie erstellt!' : 'Strategy created!');
        setShowCreateModal(false);
        setFormData({ name: '', description: '', color: STRATEGY_COLORS[0] });
        fetchStrategies();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error creating strategy');
      }
    } catch (error) {
      console.error('Error creating strategy:', error);
      toast.error(language === 'de' ? 'Fehler beim Erstellen' : 'Error creating strategy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error(language === 'de' ? 'Name ist erforderlich' : 'Name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/strategies/${selectedStrategy.strategy_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success(language === 'de' ? 'Strategie aktualisiert!' : 'Strategy updated!');
        setShowEditModal(false);
        setSelectedStrategy(null);
        fetchStrategies();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error updating strategy');
      }
    } catch (error) {
      console.error('Error updating strategy:', error);
      toast.error(language === 'de' ? 'Fehler beim Aktualisieren' : 'Error updating strategy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/strategies/${selectedStrategy.strategy_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success(language === 'de' ? 'Strategie gelöscht!' : 'Strategy deleted!');
        setShowDeleteModal(false);
        setSelectedStrategy(null);
        fetchStrategies();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error deleting strategy');
      }
    } catch (error) {
      console.error('Error deleting strategy:', error);
      toast.error(language === 'de' ? 'Fehler beim Löschen' : 'Error deleting strategy');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchStrategyStats = async (strategy) => {
    try {
      const response = await fetch(`${API}/strategies/${strategy.strategy_id}/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStrategyStats(data);
        setShowStatsModal(true);
      }
    } catch (error) {
      console.error('Error fetching strategy stats:', error);
    }
  };

  const openEditModal = (strategy) => {
    setSelectedStrategy(strategy);
    setFormData({
      name: strategy.name,
      description: strategy.description || '',
      color: strategy.color
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (strategy) => {
    setSelectedStrategy(strategy);
    setShowDeleteModal(true);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getPlanName = () => {
    const plan = user?.subscription_plan || 'basic';
    const names = { basic: 'Basis', pro: 'Pro', premium: 'Premium' };
    return names[plan] || plan;
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <Sidebar user={user} onLogout={handleLogout} />

      {/* Main Content */}
      <main className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold">
                {language === 'de' ? 'Strategien' : 'Strategies'}
              </h1>
              <p className="text-muted-foreground mt-1">
                {language === 'de' 
                  ? 'Verwalte deine Trading-Strategien und analysiere ihre Performance'
                  : 'Manage your trading strategies and analyze their performance'}
              </p>
            </div>
            
            {canCreate ? (
              <Button 
                onClick={() => {
                  setFormData({ name: '', description: '', color: STRATEGY_COLORS[Math.floor(Math.random() * STRATEGY_COLORS.length)] });
                  setShowCreateModal(true);
                }}
                className="glow-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                {language === 'de' ? 'Neue Strategie' : 'New Strategy'}
              </Button>
            ) : (
              <Button 
                variant="outline"
                onClick={() => navigate('/subscription')}
                className="border-amber-500/50 text-amber-400"
              >
                <Lock className="h-4 w-4 mr-2" />
                {language === 'de' ? 'Upgrade für mehr' : 'Upgrade for more'}
              </Button>
            )}
          </div>

          {/* Limit Info */}
          <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Target className="h-5 w-5 text-emerald-400" />
                  <span className="text-sm">
                    {strategies.length} / {strategyLimit === -1 ? '∞' : strategyLimit} {language === 'de' ? 'Strategien' : 'Strategies'}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {getPlanName()} {language === 'de' ? 'Abo' : 'Plan'}
                </span>
              </div>
              {!canCreate && strategyLimit !== -1 && (
                <p className="text-xs text-amber-400 mt-2">
                  {language === 'de' 
                    ? `Limit erreicht. Upgrade auf ${strategyLimit === 1 ? 'Pro (5)' : 'Premium (∞)'} für mehr Strategien.`
                    : `Limit reached. Upgrade to ${strategyLimit === 1 ? 'Pro (5)' : 'Premium (∞)'} for more strategies.`}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Strategies List */}
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              {language === 'de' ? 'Lade Strategien...' : 'Loading strategies...'}
            </div>
          ) : strategies.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="py-12 text-center">
                <Target className="h-12 w-12 mx-auto mb-4 text-zinc-600" />
                <h3 className="text-lg font-medium mb-2">
                  {language === 'de' ? 'Keine Strategien' : 'No Strategies'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {language === 'de' 
                    ? 'Erstelle deine erste Trading-Strategie, um Trades zu kategorisieren und zu analysieren.'
                    : 'Create your first trading strategy to categorize and analyze trades.'}
                </p>
                {canCreate && (
                  <Button onClick={() => setShowCreateModal(true)} className="glow-primary">
                    <Plus className="h-4 w-4 mr-2" />
                    {language === 'de' ? 'Erste Strategie erstellen' : 'Create First Strategy'}
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {strategies.map((strategy) => (
                <Card 
                  key={strategy.strategy_id} 
                  className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer"
                  onClick={() => fetchStrategyStats(strategy)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: strategy.color }}
                        />
                        <div>
                          <h3 className="font-medium">{strategy.name}</h3>
                          {strategy.description && (
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {strategy.description}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-medium">{strategy.trade_count}</p>
                          <p className="text-xs text-muted-foreground">
                            {language === 'de' ? 'Trades' : 'Trades'}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(strategy);
                            }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-400 hover:text-red-300"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteModal(strategy);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>{language === 'de' ? 'Neue Strategie' : 'New Strategy'}</DialogTitle>
            <DialogDescription>
              {language === 'de' 
                ? 'Erstelle eine neue Trading-Strategie'
                : 'Create a new trading strategy'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{language === 'de' ? 'Name' : 'Name'} <span className="text-red-500">*</span></Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={language === 'de' ? 'z.B. Breakout-Strategie' : 'e.g. Breakout Strategy'}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>{language === 'de' ? 'Beschreibung' : 'Description'}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={language === 'de' ? 'Beschreibe deine Strategie...' : 'Describe your strategy...'}
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div>
              <Label>{language === 'de' ? 'Farbe' : 'Color'}</Label>
              <div className="flex gap-2 mt-2">
                {STRATEGY_COLORS.map((color) => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full transition-transform ${
                      formData.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-zinc-900 scale-110' : ''
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData(prev => ({ ...prev, color }))}
                  />
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              {language === 'de' ? 'Abbrechen' : 'Cancel'}
            </Button>
            <Button onClick={handleCreate} disabled={submitting} className="glow-primary">
              {submitting ? '...' : (language === 'de' ? 'Erstellen' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>{language === 'de' ? 'Strategie bearbeiten' : 'Edit Strategy'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{language === 'de' ? 'Name' : 'Name'} <span className="text-red-500">*</span></Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>{language === 'de' ? 'Beschreibung' : 'Description'}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div>
              <Label>{language === 'de' ? 'Farbe' : 'Color'}</Label>
              <div className="flex gap-2 mt-2">
                {STRATEGY_COLORS.map((color) => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full transition-transform ${
                      formData.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-zinc-900 scale-110' : ''
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData(prev => ({ ...prev, color }))}
                  />
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              {language === 'de' ? 'Abbrechen' : 'Cancel'}
            </Button>
            <Button onClick={handleUpdate} disabled={submitting} className="glow-primary">
              {submitting ? '...' : (language === 'de' ? 'Speichern' : 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>{language === 'de' ? 'Strategie löschen?' : 'Delete Strategy?'}</DialogTitle>
            <DialogDescription>
              {language === 'de' 
                ? `Die Strategie "${selectedStrategy?.name}" wird gelöscht. Die zugewiesenen Trades bleiben erhalten, verlieren aber ihre Strategie-Zuordnung.`
                : `The strategy "${selectedStrategy?.name}" will be deleted. Assigned trades will remain but lose their strategy assignment.`}
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              {language === 'de' ? 'Abbrechen' : 'Cancel'}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={submitting}
            >
              {submitting ? '...' : (language === 'de' ? 'Löschen' : 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Modal */}
      <Dialog open={showStatsModal} onOpenChange={setShowStatsModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div 
                className="w-4 h-4 rounded-full" 
                style={{ backgroundColor: strategyStats?.strategy?.color }}
              />
              {strategyStats?.strategy?.name}
            </DialogTitle>
            {strategyStats?.strategy?.description && (
              <DialogDescription>{strategyStats?.strategy?.description}</DialogDescription>
            )}
          </DialogHeader>
          
          {strategyStats?.stats && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">{language === 'de' ? 'Trades' : 'Trades'}</p>
                  <p className="text-2xl font-bold">{strategyStats.stats.total_trades}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">{language === 'de' ? 'Win-Rate' : 'Win Rate'}</p>
                  <p className="text-2xl font-bold">{strategyStats.stats.win_rate}%</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">{language === 'de' ? 'Gesamt P&L' : 'Total P&L'}</p>
                  <p className={`text-2xl font-bold ${strategyStats.stats.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {strategyStats.stats.total_pnl >= 0 ? '+' : ''}{strategyStats.stats.total_pnl}
                  </p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">{language === 'de' ? 'Profit-Faktor' : 'Profit Factor'}</p>
                  <p className="text-2xl font-bold">{strategyStats.stats.profit_factor}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{language === 'de' ? 'Gewinner' : 'Winners'}</span>
                  <span className="text-green-400">{strategyStats.stats.winners}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{language === 'de' ? 'Verlierer' : 'Losers'}</span>
                  <span className="text-red-400">{strategyStats.stats.losers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{language === 'de' ? 'Ø Gewinn' : 'Avg Win'}</span>
                  <span className="text-green-400">+{strategyStats.stats.avg_win}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{language === 'de' ? 'Ø Verlust' : 'Avg Loss'}</span>
                  <span className="text-red-400">{strategyStats.stats.avg_loss}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{language === 'de' ? 'Bester Trade' : 'Best Trade'}</span>
                  <span className="text-green-400">+{strategyStats.stats.best_trade}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{language === 'de' ? 'Schlechtester' : 'Worst Trade'}</span>
                  <span className="text-red-400">{strategyStats.stats.worst_trade}</span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatsModal(false)}>
              {language === 'de' ? 'Schließen' : 'Close'}
            </Button>
            <Button 
              onClick={() => {
                setShowStatsModal(false);
                navigate(`/dashboard?strategy=${strategyStats?.strategy?.strategy_id}`);
              }}
              className="glow-primary"
            >
              {language === 'de' ? 'Trades anzeigen' : 'View Trades'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StrategiesPage;
